import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  ArrowLeft, 
  Heart, 
  ShoppingCart, 
  Star, 
  ChevronRight,
  ChevronDown,
  Check,
  Shield,
  Truck,
  Package,
  Leaf,
  Store,
  Home,
  Grid3X3,
  ClipboardList,
  Tag,
  User,
  Minus,
  Plus
} from 'lucide-react';
import { toast } from 'sonner';

// Sample product data
const PRODUCT_DATA = {
  id: '1',
  name: 'Brass Ganesh Idol',
  price: 1299,
  originalPrice: 1699,
  rating: 4.8,
  reviewCount: 525,
  images: [
    'https://static.prod-images.emergentagent.com/jobs/792f5e6c-8f01-4dd7-92b6-68dd9d602feb/images/f60696cdd6b05088d0faf888b49e6f1aaee3895096cdce1e651785d44194278a.png',
    'https://static.prod-images.emergentagent.com/jobs/792f5e6c-8f01-4dd7-92b6-68dd9d602feb/images/60c5190006a26195453e919f8ecee2dd0c12b7cfe4c0f09a68bb5b9fb5879f03.png',
    'https://static.prod-images.emergentagent.com/jobs/792f5e6c-8f01-4dd7-92b6-68dd9d602feb/images/8f61f1582ff5ce1e186deeadad2b05f24d66814196fde1d2c5f082c2dea6b1b1.png',
    'https://static.prod-images.emergentagent.com/jobs/792f5e6c-8f01-4dd7-92b6-68dd9d602feb/images/3afe6d01b91a3743c7cf912ababfd6fbe21bdf5f5ac0fa8baadcfe520368d817.png',
  ],
  vendor: {
    name: 'Vedic Treasures',
    rating: 4.9,
    orders: 1200,
    logo: 'https://api.dicebear.com/7.x/initials/svg?seed=VT&backgroundColor=F97316'
  },
  description: 'Beautifully crafted Brass Ganesh idol with intricate detailing, perfect for your home temple. Brings blessings of wisdom, prosperity, and good fortune.',
  specs: [
    { label: 'Height', value: '4 inches (approx.)' },
    { label: 'Material', value: 'Brass with antique finish' },
    { label: 'Weight', value: '350 grams (approx.)' },
    { label: 'Origin', value: 'Crafted in Varanasi' },
    { label: 'Craft Type', value: 'Traditional lost wax casting' },
  ],
  usage: 'Perfect for home temple, meditation altar, or as a gift.',
};

// Frequently bought together
const FREQUENTLY_BOUGHT = [
  { id: '1', name: 'Brass Ganesh Idol', price: 1299, image: 'https://static.prod-images.emergentagent.com/jobs/792f5e6c-8f01-4dd7-92b6-68dd9d602feb/images/f60696cdd6b05088d0faf888b49e6f1aaee3895096cdce1e651785d44194278a.png', selected: true },
  { id: '2', name: 'Brass Diya', price: 499, image: 'https://static.prod-images.emergentagent.com/jobs/792f5e6c-8f01-4dd7-92b6-68dd9d602feb/images/60c5190006a26195453e919f8ecee2dd0c12b7cfe4c0f09a68bb5b9fb5879f03.png', selected: true },
  { id: '3', name: 'Pooja Kit', price: 699, image: 'https://static.prod-images.emergentagent.com/jobs/792f5e6c-8f01-4dd7-92b6-68dd9d602feb/images/8f61f1582ff5ce1e186deeadad2b05f24d66814196fde1d2c5f082c2dea6b1b1.png', selected: true },
];

// You may also like
const SIMILAR_PRODUCTS = [
  { id: '4', name: "6' Brass Lakshmi Idol", price: 1899, rating: 4.7, image: 'https://static.prod-images.emergentagent.com/jobs/792f5e6c-8f01-4dd7-92b6-68dd9d602feb/images/3afe6d01b91a3743c7cf912ababfd6fbe21bdf5f5ac0fa8baadcfe520368d817.png' },
  { id: '5', name: 'Panchdhatu Ganesh', price: 2499, rating: 4.9, image: 'https://static.prod-images.emergentagent.com/jobs/792f5e6c-8f01-4dd7-92b6-68dd9d602feb/images/f60696cdd6b05088d0faf888b49e6f1aaee3895096cdce1e651785d44194278a.png' },
  { id: '6', name: '8 Mukhi Rudraksha Mala', price: 999, rating: 4.8, image: 'https://static.prod-images.emergentagent.com/jobs/792f5e6c-8f01-4dd7-92b6-68dd9d602feb/images/60c5190006a26195453e919f8ecee2dd0c12b7cfe4c0f09a68bb5b9fb5879f03.png' },
];

// Vendor products
const VENDOR_PRODUCTS = [
  { id: '7', name: 'Silver Pooja Thali', price: 1599, image: 'https://static.prod-images.emergentagent.com/jobs/792f5e6c-8f01-4dd7-92b6-68dd9d602feb/images/8f61f1582ff5ce1e186deeadad2b05f24d66814196fde1d2c5f082c2dea6b1b1.png' },
  { id: '8', name: 'Copper Kalash', price: 899, image: 'https://static.prod-images.emergentagent.com/jobs/792f5e6c-8f01-4dd7-92b6-68dd9d602feb/images/3afe6d01b91a3743c7cf912ababfd6fbe21bdf5f5ac0fa8baadcfe520368d817.png' },
  { id: '9', name: 'Brass Bell', price: 399, image: 'https://static.prod-images.emergentagent.com/jobs/792f5e6c-8f01-4dd7-92b6-68dd9d602feb/images/f60696cdd6b05088d0faf888b49e6f1aaee3895096cdce1e651785d44194278a.png' },
];

export default function ProductDetailPage() {
  const navigate = useNavigate();
  const { productId } = useParams();
  const [currentImage, setCurrentImage] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [isWishlisted, setIsWishlisted] = useState(false);
  const [showFullDescription, setShowFullDescription] = useState(false);
  const [frequentlyBought, setFrequentlyBought] = useState(FREQUENTLY_BOUGHT);
  const [cartCount, setCartCount] = useState(1);

  const product = PRODUCT_DATA;

  const toggleFrequentlyBought = (id) => {
    setFrequentlyBought(prev => 
      prev.map(item => item.id === id ? { ...item, selected: !item.selected } : item)
    );
  };

  const frequentlyBoughtTotal = frequentlyBought
    .filter(item => item.selected)
    .reduce((sum, item) => sum + item.price, 0);

  const handleAddToCart = () => {
    setCartCount(prev => prev + quantity);
    toast.success(`${product.name} added to cart!`);
  };

  const handleBuyNow = () => {
    toast.success('Proceeding to checkout...');
    navigate('/shop/checkout');
  };

  const renderStars = (rating) => {
    return (
      <div className="flex items-center gap-0.5">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`w-4 h-4 ${star <= Math.floor(rating) ? 'fill-amber-400 text-amber-400' : 'text-stone-300'}`}
          />
        ))}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#FDF6ED] to-[#F5EDE3]">
      {/* Header - App Style with Icon Navigation */}
      <header className="sticky top-0 bg-white border-b border-amber-100 z-50 shadow-sm">
        <div className="flex items-center justify-between px-3 h-14">
          {/* Left: App Navigation Icons */}
          <div className="flex items-center gap-2">
            {/* Home Icon */}
            <button 
              onClick={() => navigate('/')}
              className="w-10 h-10 rounded-full flex items-center justify-center text-amber-700 hover:bg-amber-50 transition-colors"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"/>
                <polyline points="9 22 9 12 15 12 15 22"/>
              </svg>
            </button>
            
            {/* Shop Icon - Active State with Orange Circle */}
            <button 
              onClick={() => navigate('/shop')}
              className="w-10 h-10 rounded-full bg-gradient-to-br from-orange-500 to-amber-500 flex items-center justify-center shadow-md"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5">
                <path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z"/>
                <line x1="3" y1="6" x2="21" y2="6"/>
                <path d="M16 10a4 4 0 01-8 0"/>
              </svg>
            </button>
          </div>

          {/* Right: Search, Cart, Profile */}
          <div className="flex items-center gap-1">
            {/* Search */}
            <button 
              onClick={() => navigate('/shop/search')}
              className="w-10 h-10 rounded-full flex items-center justify-center text-amber-700 hover:bg-amber-50 transition-colors"
            >
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="8"/>
                <line x1="21" y1="21" x2="16.65" y2="16.65"/>
              </svg>
            </button>

            {/* Cart with Badge */}
            <button 
              onClick={() => navigate('/shop/cart')}
              className="w-10 h-10 rounded-full flex items-center justify-center text-amber-700 hover:bg-amber-50 transition-colors relative"
            >
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M9 22a1 1 0 100-2 1 1 0 000 2zM20 22a1 1 0 100-2 1 1 0 000 2z" fill="currentColor"/>
                <path d="M1 1h4l2.68 13.39a2 2 0 002 1.61h9.72a2 2 0 002-1.61L23 6H6"/>
              </svg>
              {cartCount > 0 && (
                <span className="absolute -top-0.5 -right-0.5 w-5 h-5 bg-orange-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                  {cartCount}
                </span>
              )}
            </button>

            {/* Profile Avatar */}
            <button 
              onClick={() => navigate('/shop/account')}
              className="w-9 h-9 rounded-full overflow-hidden border-2 border-amber-200"
            >
              <img 
                src="https://api.dicebear.com/7.x/avataaars/svg?seed=User" 
                alt="Profile" 
                className="w-full h-full object-cover"
              />
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="pb-40">
        {/* Product Image Carousel */}
        <div className="bg-white">
          <div className="relative aspect-square overflow-hidden">
            <img 
              src={product.images[currentImage]} 
              alt={product.name}
              className="w-full h-full object-cover"
            />
            {/* Image indicators */}
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
              {product.images.map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => setCurrentImage(idx)}
                  className={`w-2 h-2 rounded-full transition-all ${
                    idx === currentImage ? 'bg-amber-600 w-6' : 'bg-white/70'
                  }`}
                />
              ))}
            </div>
          </div>
          
          {/* Thumbnail Strip */}
          <div className="flex gap-2 p-3 overflow-x-auto">
            {product.images.map((img, idx) => (
              <button
                key={idx}
                onClick={() => setCurrentImage(idx)}
                className={`w-16 h-16 rounded-lg overflow-hidden flex-shrink-0 border-2 transition-all ${
                  idx === currentImage ? 'border-amber-500' : 'border-transparent'
                }`}
              >
                <img src={img} alt="" className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
        </div>

        {/* Product Info */}
        <div className="px-4 py-4">
          <h1 className="text-2xl font-bold text-amber-900" style={{ fontFamily: 'serif' }}>
            {product.name}
          </h1>
          
          <div className="flex items-baseline gap-3 mt-2">
            <span className="text-2xl font-bold text-amber-800">₹{product.price}</span>
            <span className="text-lg text-stone-400 line-through">₹{product.originalPrice}</span>
            <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs font-semibold rounded">
              {Math.round((1 - product.price / product.originalPrice) * 100)}% OFF
            </span>
          </div>

          <div className="flex items-center gap-2 mt-2">
            {renderStars(product.rating)}
            <span className="text-amber-700 font-medium">{product.rating}</span>
            <span className="text-stone-500">({product.reviewCount} Reviews)</span>
          </div>
        </div>

        {/* Vendor Block */}
        <div className="mx-4 p-4 bg-white rounded-2xl border border-amber-100 shadow-sm">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img 
                src={product.vendor.logo} 
                alt={product.vendor.name}
                className="w-12 h-12 rounded-xl"
              />
              <div>
                <p className="font-semibold text-amber-900">{product.vendor.name}</p>
                <div className="flex items-center gap-1 text-sm text-stone-500">
                  <Star className="w-3 h-3 fill-amber-400 text-amber-400" />
                  <span>{product.vendor.rating}</span>
                  <span>•</span>
                  <span>({product.vendor.orders} Orders)</span>
                </div>
              </div>
            </div>
            <button className="px-4 py-2 border border-amber-500 text-amber-600 rounded-xl font-medium text-sm hover:bg-amber-50 transition-colors">
              Visit Store
            </button>
          </div>
        </div>

        {/* Trust Badges */}
        <div className="grid grid-cols-4 gap-2 px-4 py-4">
          {[
            { icon: '🇮🇳', label: 'Made in India' },
            { icon: Shield, label: 'Secure Payments' },
            { icon: Truck, label: 'Pan-India Delivery' },
            { icon: Leaf, label: 'Eco Packaging' },
          ].map((badge, idx) => (
            <div key={idx} className="flex flex-col items-center gap-1 p-2 bg-amber-50/50 rounded-xl">
              {typeof badge.icon === 'string' ? (
                <span className="text-2xl">{badge.icon}</span>
              ) : (
                <badge.icon className="w-5 h-5 text-amber-600" />
              )}
              <span className="text-[10px] text-amber-700 text-center font-medium leading-tight">{badge.label}</span>
            </div>
          ))}
        </div>

        {/* Product Details */}
        <div className="mx-4 p-4 bg-white rounded-2xl border border-amber-100 shadow-sm">
          <h2 className="text-lg font-bold text-amber-900 mb-3" style={{ fontFamily: 'serif' }}>
            Product Details
          </h2>
          
          <p className={`text-stone-600 text-sm leading-relaxed ${!showFullDescription ? 'line-clamp-3' : ''}`}>
            {product.description}
          </p>
          
          <ul className="mt-3 space-y-2">
            {product.specs.slice(0, showFullDescription ? undefined : 3).map((spec, idx) => (
              <li key={idx} className="flex items-start gap-2 text-sm">
                <span className="w-1.5 h-1.5 rounded-full bg-amber-500 mt-1.5 flex-shrink-0" />
                <span><strong className="text-amber-800">{spec.label}:</strong> <span className="text-stone-600">{spec.value}</span></span>
              </li>
            ))}
          </ul>

          <button 
            onClick={() => setShowFullDescription(!showFullDescription)}
            className="flex items-center gap-1 text-amber-600 font-medium text-sm mt-3"
          >
            {showFullDescription ? 'Show Less' : 'Read More'}
            <ChevronDown className={`w-4 h-4 transition-transform ${showFullDescription ? 'rotate-180' : ''}`} />
          </button>
        </div>

        {/* Frequently Bought Together */}
        <div className="mx-4 mt-4 p-4 bg-white rounded-2xl border border-amber-100 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-bold text-amber-900" style={{ fontFamily: 'serif' }}>
              Frequently Bought Together
            </h2>
            <ChevronRight className="w-5 h-5 text-amber-400" />
          </div>

          <div className="flex gap-3 overflow-x-auto pb-2">
            {frequentlyBought.map((item) => (
              <div key={item.id} className="flex-shrink-0 w-28">
                <button
                  onClick={() => toggleFrequentlyBought(item.id)}
                  className={`relative w-full aspect-square rounded-xl overflow-hidden border-2 transition-all ${
                    item.selected ? 'border-amber-500' : 'border-stone-200'
                  }`}
                >
                  <img src={item.image} alt={item.name} className="w-full h-full object-cover" />
                  <div className={`absolute top-2 left-2 w-5 h-5 rounded-md flex items-center justify-center ${
                    item.selected ? 'bg-amber-500' : 'bg-white border border-stone-300'
                  }`}>
                    {item.selected && <Check className="w-3 h-3 text-white" />}
                  </div>
                </button>
                <p className="text-xs font-medium text-amber-800 mt-1 truncate">{item.name}</p>
                <p className="text-xs text-amber-600 font-bold">₹{item.price}</p>
              </div>
            ))}
          </div>

          <button 
            onClick={() => {
              setCartCount(prev => prev + frequentlyBought.filter(i => i.selected).length);
              toast.success('Bundle added to cart!');
            }}
            className="w-full mt-4 py-3 bg-gradient-to-r from-amber-600 to-amber-500 text-white rounded-xl font-semibold flex items-center justify-center gap-2 shadow-lg shadow-amber-200"
          >
            <span>Add All to Cart</span>
            <span className="px-2 py-0.5 bg-white/20 rounded-lg text-sm">₹{frequentlyBoughtTotal}</span>
          </button>
        </div>

        {/* You May Also Like */}
        <div className="mt-6 px-4">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-bold text-amber-900" style={{ fontFamily: 'serif' }}>
              You May Also Like
            </h2>
            <ChevronRight className="w-5 h-5 text-amber-400" />
          </div>

          <div className="flex gap-3 overflow-x-auto pb-2">
            {SIMILAR_PRODUCTS.map((item) => (
              <div 
                key={item.id} 
                className="flex-shrink-0 w-36 bg-white rounded-2xl overflow-hidden border border-amber-100 shadow-sm"
              >
                <div className="aspect-square overflow-hidden">
                  <img src={item.image} alt={item.name} className="w-full h-full object-cover" />
                </div>
                <div className="p-2">
                  <p className="text-sm font-medium text-amber-800 truncate">{item.name}</p>
                  <div className="flex items-center justify-between mt-1">
                    <span className="text-amber-700 font-bold text-sm">₹{item.price}</span>
                    <div className="flex items-center gap-0.5">
                      <Star className="w-3 h-3 fill-amber-400 text-amber-400" />
                      <span className="text-xs text-stone-500">{item.rating}</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* From This Vendor */}
        <div className="mt-6 px-4">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-bold text-amber-900" style={{ fontFamily: 'serif' }}>
              From {product.vendor.name}
            </h2>
            <ChevronRight className="w-5 h-5 text-amber-400" />
          </div>

          <div className="flex gap-3 overflow-x-auto pb-2">
            {VENDOR_PRODUCTS.map((item) => (
              <div 
                key={item.id} 
                className="flex-shrink-0 w-28 bg-white rounded-xl overflow-hidden border border-amber-100 shadow-sm"
              >
                <div className="aspect-square overflow-hidden">
                  <img src={item.image} alt={item.name} className="w-full h-full object-cover" />
                </div>
                <div className="p-2">
                  <p className="text-xs font-medium text-amber-800 truncate">{item.name}</p>
                  <span className="text-amber-700 font-bold text-xs">₹{item.price}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Support Swadeshi Banner */}
        <div className="mx-4 mt-6 p-4 bg-gradient-to-r from-amber-50 to-orange-50 rounded-2xl border border-amber-200">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-full bg-amber-100 flex items-center justify-center text-3xl">
              🧑‍🎨
            </div>
            <div>
              <h3 className="font-bold text-amber-900" style={{ fontFamily: 'serif' }}>Support Swadeshi Artisans</h3>
              <p className="text-sm text-amber-700 mt-0.5">Every purchase supports traditional Indian artisans and promotes Swadeshi.</p>
            </div>
          </div>
        </div>
      </main>

      {/* Sticky Bottom Bar */}
      <div className="fixed bottom-16 left-0 right-0 bg-white border-t border-amber-100 p-3 shadow-lg z-40">
        <div className="flex items-center gap-3">
          {/* Quantity Selector */}
          <div className="flex items-center border border-amber-200 rounded-xl overflow-hidden bg-amber-50">
            <button 
              onClick={() => setQuantity(Math.max(1, quantity - 1))}
              className="w-10 h-10 flex items-center justify-center text-amber-700 hover:bg-amber-100 transition-colors"
            >
              <Minus className="w-4 h-4" />
            </button>
            <span className="w-10 text-center font-semibold text-amber-800">{quantity}</span>
            <button 
              onClick={() => setQuantity(quantity + 1)}
              className="w-10 h-10 flex items-center justify-center text-amber-700 hover:bg-amber-100 transition-colors"
            >
              <Plus className="w-4 h-4" />
            </button>
          </div>

          {/* Add to Cart */}
          <button 
            onClick={handleAddToCart}
            className="flex-1 py-3 border-2 border-amber-500 text-amber-600 rounded-xl font-bold hover:bg-amber-50 transition-colors"
          >
            Add to Cart
          </button>

          {/* Buy Now */}
          <button 
            onClick={handleBuyNow}
            className="flex-1 py-3 bg-gradient-to-r from-orange-500 to-amber-500 text-white rounded-xl font-bold shadow-lg shadow-amber-200"
          >
            Buy Now
          </button>
        </div>
      </div>

      {/* Bottom Navigation - Amazon Style Thin Line Icons */}
      <nav className="fixed bottom-0 left-0 right-0 bg-white border-t border-stone-200 z-50">
        <div className="grid grid-cols-5 h-16">
          {/* Home - Amazon style house icon */}
          <button
            onClick={() => navigate('/shop')}
            className="flex flex-col items-center justify-center gap-1 text-stone-800"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M3 10.5L12 3l9 7.5"/>
              <path d="M5 9v10a1 1 0 001 1h12a1 1 0 001-1V9"/>
            </svg>
            <span className="text-[10px] font-medium">Home</span>
          </button>

          {/* You - Amazon style person icon */}
          <button
            onClick={() => navigate('/shop/account')}
            className="flex flex-col items-center justify-center gap-1 text-stone-500"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="7" r="4"/>
              <path d="M5.5 21a6.5 6.5 0 0113 0"/>
            </svg>
            <span className="text-[10px] font-medium">You</span>
          </button>

          {/* Wallet - with notification dot */}
          <button
            onClick={() => navigate('/shop/wallet')}
            className="flex flex-col items-center justify-center gap-1 text-stone-500 relative"
          >
            <div className="relative">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <rect x="2" y="6" width="20" height="14" rx="2"/>
                <path d="M2 10h20"/>
                <circle cx="17" cy="14" r="2"/>
              </svg>
              <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-red-500 rounded-full"></span>
            </div>
            <span className="text-[10px] font-medium">Wallet</span>
          </button>

          {/* Cart - with badge */}
          <button
            onClick={() => navigate('/shop/cart')}
            className="flex flex-col items-center justify-center gap-1 text-stone-500 relative"
          >
            <div className="relative">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M4 4h2l3.6 12h8.8l2.6-8H8"/>
                <circle cx="10" cy="20" r="1.5"/>
                <circle cx="18" cy="20" r="1.5"/>
              </svg>
              {cartCount > 0 && (
                <span className="absolute -top-2 -right-2 w-5 h-5 bg-orange-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                  {cartCount}
                </span>
              )}
            </div>
            <span className="text-[10px] font-medium">Cart</span>
          </button>

          {/* Menu - hamburger icon */}
          <button
            onClick={() => navigate('/shop/menu')}
            className="flex flex-col items-center justify-center gap-1 text-stone-500"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
              <line x1="4" y1="6" x2="20" y2="6"/>
              <line x1="4" y1="12" x2="20" y2="12"/>
              <line x1="4" y1="18" x2="20" y2="18"/>
            </svg>
            <span className="text-[10px] font-medium">Menu</span>
          </button>
        </div>
      </nav>
    </div>
  );
}
